import React, { useState } from 'react';

// ── Câu hỏi & lựa chọn ────────────────────────────────────────────────────────
interface Option { label: string; sub?: string; score: number }
interface Question {
  id:          number;
  title:       string;
  hint?:       string;          // gợi ý hiển thị dưới tiêu đề
  multiSelect?: boolean;        // cho phép chọn nhiều đáp án
  options:     Option[];
}

const QUESTIONS: Question[] = [
  {
    id: 1,
    title: 'Mục tiêu tài chính của bạn là gì?',
    options: [
      { label: 'Bảo toàn 100% vốn',      sub: 'Không chấp nhận bất kỳ rủi ro nào',       score: 1 },
      { label: 'Sinh lãi ổn định',        sub: 'Tương đương lãi suất ngân hàng',           score: 2 },
      { label: 'Tăng trưởng cân bằng',    sub: 'Chấp nhận biến động vừa phải',            score: 3 },
      { label: 'Tăng trưởng cao',         sub: 'Chấp nhận biến động lớn trong dài hạn',   score: 4 },
      { label: 'Tối đa hóa lợi nhuận',    sub: 'Bất kể mức độ biến động ngắn hạn',        score: 5 },
    ],
  },
  {
    id: 2,
    title: 'Nếu danh mục giảm 20% trong 1 tháng, bạn sẽ làm gì?',
    options: [
      { label: 'Bán ngay toàn bộ',        sub: 'Chuyển hết sang tiền mặt an toàn',        score: 1 },
      { label: 'Bán bớt một phần',        sub: 'Giảm tỷ lệ rủi ro xuống',                 score: 2 },
      { label: 'Giữ nguyên',              sub: 'Đợi thị trường phục hồi',                  score: 3 },
      { label: 'Mua thêm một ít',         sub: 'Giá đang tốt, tích lũy thêm',             score: 4 },
      { label: 'Mua mạnh tay',            sub: 'Đây là cơ hội lớn, không thể bỏ qua',     score: 5 },
    ],
  },
  {
    id: 3,
    title: 'Thời gian đầu tư dự kiến của bạn?',
    options: [
      { label: 'Dưới 1 năm',              sub: 'Cần tiền trong thời gian ngắn',            score: 1 },
      { label: '1 – 3 năm',              sub: 'Kế hoạch trung hạn',                       score: 2 },
      { label: '3 – 7 năm',              sub: 'Đầu tư dài hạn có kế hoạch',               score: 3 },
      { label: '7 – 15 năm',             sub: 'Xây dựng tài sản bền vững',                score: 4 },
      { label: 'Trên 15 năm',            sub: 'Hoặc để lại cho thế hệ sau',               score: 5 },
    ],
  },
  {
    id: 4,
    title: 'Bạn sẵn sàng đặt bao nhiêu % tổng tài sản vào kênh rủi ro cao?',
    options: [
      { label: '0 – 10%',                sub: 'Phần lớn giữ tiền mặt và tiết kiệm',       score: 1 },
      { label: '10 – 25%',               sub: 'Đa dạng hóa nhẹ với cổ phiếu',             score: 2 },
      { label: '25 – 50%',               sub: 'Cân bằng giữa an toàn và tăng trưởng',     score: 3 },
      { label: '50 – 75%',               sub: 'Nghiêng về tăng trưởng mạnh',              score: 4 },
      { label: 'Trên 75%',               sub: 'Tập trung tối đa vào tài sản tăng trưởng', score: 5 },
    ],
  },
  {
    id: 5,
    title: 'Bạn đã có kinh nghiệm với kênh đầu tư nào?',
    hint: 'Có thể chọn nhiều',
    multiSelect: true,
    options: [
      { label: 'Chưa từng đầu tư',        sub: 'Mới bắt đầu tìm hiểu',                    score: 1 },
      { label: 'Tiết kiệm / sổ tiết kiệm', sub: 'Gửi ngân hàng, kỳ phiếu',               score: 2 },
      { label: 'Cổ phiếu, trái phiếu',    sub: 'Đã giao dịch trên sàn chứng khoán',       score: 3 },
      { label: 'BĐS, quỹ đầu tư',         sub: 'Bất động sản, quỹ mở, ETF',               score: 4 },
      { label: 'Crypto, phái sinh',        sub: 'Hiểu rõ rủi ro, giao dịch nâng cao',      score: 5 },
    ],
  },
];

// ── Risk profile ──────────────────────────────────────────────────────────────
interface RiskProfile {
  label:     string;
  color:     string;
  desc:      string;
  allocation: string;   // gợi ý phân bổ tài sản
  uses:      string[];  // cách MoBu dùng hồ sơ này
}

function getRiskProfile(avg: number): RiskProfile {
  if (avg <= 1.8) return {
    label: 'Thận trọng', color: '#38bdf8',
    desc:  'Bạn ưu tiên bảo toàn vốn và sự ổn định tuyệt đối.',
    allocation: 'Tiền mặt & tiết kiệm 60–80% · Thu nhập cố định 15–30% · Cổ phiếu ≤10%',
    uses: [
      'Đề xuất phân bổ chiến lược (SAA) nghiêng về tài sản an toàn',
      'Cảnh báo khi tỷ lệ tài sản rủi ro cao vượt ngưỡng của bạn',
      'FIRE Calculator dùng lãi suất kỳ vọng thận trọng (~5%/năm)',
      'Nhắc nhở tái cân bằng danh mục về mức phù hợp',
    ],
  };
  if (avg <= 2.6) return {
    label: 'Bảo thủ', color: '#34d399',
    desc:  'Bạn muốn tăng trưởng ổn định với biến động được kiểm soát.',
    allocation: 'Thu nhập cố định 40–55% · Cổ phiếu phòng thủ 25–35% · Tiền mặt 15–25%',
    uses: [
      'SAA gợi ý danh mục cân bằng nghiêng phòng thủ',
      'FIRE Calculator dùng lãi suất kỳ vọng ~7%/năm',
      'Báo động khi biến động danh mục vượt mức bạn chịu được',
      'Ưu tiên hiển thị tài sản thu nhập cố định trong Dashboard',
    ],
  };
  if (avg <= 3.4) return {
    label: 'Cân bằng', color: '#a78bfa',
    desc:  'Bạn chấp nhận biến động vừa phải để đổi lấy tăng trưởng dài hạn.',
    allocation: 'Cổ phiếu 40–55% · Thu nhập cố định 25–35% · BĐS / Quỹ 10–20%',
    uses: [
      'SAA gợi ý danh mục 60/40 cổ phiếu–trái phiếu kinh điển',
      'FIRE Calculator dùng lãi suất kỳ vọng ~9%/năm',
      'Phân tích hiệu suất so sánh với benchmark cân bằng',
      'Gợi ý cơ hội tái phân bổ khi thị trường có biến động lớn',
    ],
  };
  if (avg <= 4.2) return {
    label: 'Năng động', color: '#fb923c',
    desc:  'Bạn sẵn sàng chấp nhận rủi ro đáng kể để tối ưu lợi nhuận dài hạn.',
    allocation: 'Cổ phiếu tăng trưởng 55–70% · BĐS / Quỹ 15–25% · Thu nhập cố định ≤15%',
    uses: [
      'SAA thiên về tài sản tăng trưởng và thị trường mới nổi',
      'FIRE Calculator dùng lãi suất kỳ vọng ~11%/năm',
      'Phân tích Sharpe Ratio và drawdown tối đa của danh mục',
      'Gợi ý cơ hội DCA khi thị trường điều chỉnh mạnh',
    ],
  };
  return {
    label: 'Tăng trưởng mạnh', color: '#f43f5e',
    desc:  'Bạn tập trung tối đa hóa tăng trưởng dài hạn, chấp nhận biến động cao.',
    allocation: 'Cổ phiếu tập trung / ETF 70–85% · BĐS 10–20% · Tài sản thay thế ≤15%',
    uses: [
      'SAA tập trung vào tài sản tăng trưởng cao và phi truyền thống',
      'FIRE Calculator dùng lãi suất kỳ vọng ~13%/năm',
      'Phân tích tập trung danh mục và rủi ro tương quan',
      'Gợi ý chiến lược leverage và đòn bẩy có kiểm soát',
    ],
  };
}

// ── Moon Crescent mini ────────────────────────────────────────────────────────
function MoonMini() {
  return (
    <svg width="36" height="36" viewBox="0 0 52 52" fill="none">
      <defs>
        <radialGradient id="ob-mg" cx="38%" cy="32%" r="65%">
          <stop offset="0%"   stopColor="#e8f8ff" />
          <stop offset="45%"  stopColor="#b8e8ff" />
          <stop offset="100%" stopColor="#60c8f0" />
        </radialGradient>
        <filter id="ob-glow" x="-60%" y="-60%" width="220%" height="220%">
          <feGaussianBlur in="SourceGraphic" stdDeviation="3" result="blur" />
          <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
        </filter>
      </defs>
      <circle cx="26" cy="26" r="23" fill="rgba(0,209,255,0.06)" />
      <g filter="url(#ob-glow)">
        <circle cx="26" cy="26" r="18" fill="url(#ob-mg)" />
        <circle cx="33" cy="22" r="14" fill="#0A192F" />
      </g>
      <circle cx="26" cy="26" r="18" fill="none" stroke="rgba(180,240,255,0.2)" strokeWidth="0.8" />
      <ellipse cx="19" cy="20" rx="3.5" ry="2" fill="rgba(255,255,255,0.18)"
        style={{ transform: 'rotate(-30deg)', transformOrigin: '19px 20px' }} />
    </svg>
  );
}

// ── Props ─────────────────────────────────────────────────────────────────────
interface OnboardingFlowProps {
  onComplete: (score: number) => Promise<void>;
}

// ── Component chính ───────────────────────────────────────────────────────────
export function OnboardingFlow({ onComplete }: OnboardingFlowProps) {
  const [step,          setStep]         = useState(0);
  const [answers,       setAnswers]       = useState<number[]>([]);
  const [selected,      setSelected]      = useState<number | null>(null);   // single-choice
  const [selectedMulti, setSelectedMulti] = useState<number[]>([]);          // multi-choice (Q5)
  const [saving,        setSaving]        = useState(false);
  const [animDir,       setAnimDir]       = useState<'in' | 'out'>('in');

  const totalSteps = QUESTIONS.length;
  const isIntro    = step === 0;
  const isResult   = step > totalSteps;
  const question   = isIntro || isResult ? null : QUESTIONS[step - 1];
  const isMulti    = question?.multiSelect === true;

  const avgScore = answers.length > 0
    ? Math.round(answers.reduce((s, a) => s + a, 0) / answers.length)
    : 3;
  const profile = getRiskProfile(avgScore);

  // Có thể đi tiếp không?
  const canProceed = isIntro || (isMulti ? selectedMulti.length > 0 : selected !== null);

  // ── Điều hướng ──────────────────────────────────────────────────────────────
  function goNext() {
    if (!canProceed) return;
    setAnimDir('out');
    setTimeout(() => {
      if (isIntro) {
        setStep(1);
      } else if (question) {
        // Tính score cho câu hiện tại
        const score = isMulti
          ? Math.max(...selectedMulti)   // multi: lấy kinh nghiệm cao nhất
          : selected!;
        setAnswers(prev => [...prev, score]);
        setSelected(null);
        setSelectedMulti([]);
        setStep(s => s + 1);
      }
      setAnimDir('in');
    }, 160);
  }

  function goBack() {
    if (step <= 1) return;
    setAnimDir('out');
    setTimeout(() => {
      setAnswers(a => a.slice(0, -1));
      setSelected(null);
      setSelectedMulti([]);
      setStep(s => s - 1);
      setAnimDir('in');
    }, 160);
  }

  function toggleMulti(score: number) {
    // Nếu chọn "Chưa từng đầu tư" (score=1) → chỉ giữ lại lựa chọn đó
    if (score === 1) {
      setSelectedMulti(prev => prev.includes(1) ? [] : [1]);
      return;
    }
    // Ngược lại, bỏ "Chưa từng đầu tư" nếu đang có
    setSelectedMulti(prev => {
      const next = prev.filter(s => s !== 1);
      return next.includes(score) ? next.filter(s => s !== score) : [...next, score];
    });
  }

  async function handleFinish() {
    setSaving(true);
    await onComplete(avgScore);
    setSaving(false);
  }

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9998,
      backgroundColor: '#0A192F',
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      justifyContent: 'center',
      padding: '24px 16px',
      overflowY: 'auto',
      fontFamily: "'Inter', -apple-system, sans-serif",
    }}>
      {/* Ocean depth glow */}
      <div style={{
        position: 'fixed', inset: 0, pointerEvents: 'none',
        background: 'radial-gradient(ellipse 70% 50% at 50% 60%, rgba(21,80,128,0.35) 0%, transparent 70%)',
      }} />

      {/* Header */}
      <div style={{
        position: 'relative', zIndex: 1,
        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8,
        marginBottom: 24, flexShrink: 0,
      }}>
        <MoonMini />
        <div style={{ fontSize: 11, letterSpacing: '0.14em', color: 'rgba(160,200,235,0.5)', textTransform: 'uppercase' }}>
          MoBu · Khởi tạo hồ sơ
        </div>
      </div>

      {/* Step progress */}
      {!isIntro && !isResult && (
        <div style={{ display: 'flex', gap: 7, marginBottom: 20, position: 'relative', zIndex: 1, flexShrink: 0 }}>
          {QUESTIONS.map((_, i) => (
            <div key={i} style={{
              width: i < step ? 20 : 7, height: 7, borderRadius: 4,
              backgroundColor: i < step ? '#00D1FF' : 'rgba(255,255,255,0.12)',
              transition: 'all 0.3s ease',
            }} />
          ))}
        </div>
      )}

      {/* Card */}
      <div style={{
        position: 'relative', zIndex: 1,
        width: '100%', maxWidth: 520,
        background: 'rgba(255,255,255,0.04)',
        backdropFilter: 'blur(20px)',
        border: '1px solid rgba(255,255,255,0.08)',
        borderRadius: 20,
        padding: '28px 24px',
        opacity: animDir === 'out' ? 0 : 1,
        transform: animDir === 'out' ? 'translateY(6px)' : 'translateY(0)',
        transition: 'opacity 0.16s ease, transform 0.16s ease',
      }}>

        {/* ── INTRO ── */}
        {isIntro && (
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 21, fontWeight: 700, color: '#C8E4F8', marginBottom: 12, lineHeight: 1.35 }}>
              Chào mừng đến với không gian gia sản của bạn
            </div>
            <div style={{ fontSize: 14, color: 'rgba(160,200,235,0.7)', lineHeight: 1.75, marginBottom: 24 }}>
              Hãy dành 2 phút để MoBu hiểu rõ khẩu vị rủi ro của bạn. Kết quả sẽ
              cá nhân hóa chiến lược phân bổ tài sản và các gợi ý đầu tư trong toàn bộ ứng dụng.
            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap', marginBottom: 24 }}>
              {['5 câu hỏi', '~2 phút', 'Có thể cập nhật sau'].map(tag => (
                <span key={tag} style={{
                  fontSize: 12, color: '#00D1FF',
                  border: '1px solid rgba(0,209,255,0.25)',
                  borderRadius: 20, padding: '4px 12px',
                  background: 'rgba(0,209,255,0.06)',
                }}>{tag}</span>
              ))}
            </div>
            <button onClick={goNext} style={btnStyle('#00D1FF')}>
              Bắt đầu
            </button>
          </div>
        )}

        {/* ── CÂU HỎI ── */}
        {question && (
          <div>
            <div style={{ fontSize: 11, color: 'rgba(160,200,235,0.45)', marginBottom: 8, letterSpacing: '0.08em' }}>
              CÂU {step} / {totalSteps}
            </div>
            <div style={{ fontSize: 16, fontWeight: 600, color: '#C8E4F8', lineHeight: 1.45, marginBottom: 4 }}>
              {question.title}
            </div>
            {question.hint && (
              <div style={{ fontSize: 12, color: '#00D1FF', marginBottom: 16, opacity: 0.8 }}>
                ✦ {question.hint}
              </div>
            )}
            {!question.hint && <div style={{ marginBottom: 18 }} />}

            {/* Options */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {question.options.map(opt => {
                const active = isMulti
                  ? selectedMulti.includes(opt.score)
                  : selected === opt.score;
                return (
                  <button
                    key={opt.score}
                    onClick={() => isMulti ? toggleMulti(opt.score) : setSelected(opt.score)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 12,
                      padding: '11px 14px',
                      borderRadius: 11,
                      border: active ? '1px solid rgba(0,209,255,0.55)' : '1px solid rgba(255,255,255,0.07)',
                      background: active ? 'rgba(0,209,255,0.08)' : 'rgba(255,255,255,0.025)',
                      cursor: 'pointer',
                      transition: 'all 0.14s ease',
                      textAlign: 'left',
                      width: '100%',
                    }}
                  >
                    {/* Checkbox / radio indicator */}
                    <div style={{
                      width: 18, height: 18, borderRadius: isMulti ? 5 : 9,
                      border: active ? '2px solid #00D1FF' : '2px solid rgba(255,255,255,0.2)',
                      background: active ? '#00D1FF' : 'transparent',
                      flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
                      transition: 'all 0.14s ease',
                    }}>
                      {active && (
                        <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
                          <path d="M1 4L3.5 6.5L9 1" stroke="#0A192F" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      )}
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                      <span style={{ fontSize: 13.5, fontWeight: 500, color: active ? '#00D1FF' : '#C8E4F8' }}>
                        {opt.label}
                      </span>
                      {opt.sub && (
                        <span style={{ fontSize: 11.5, color: 'rgba(160,200,235,0.5)', marginTop: 1 }}>
                          {opt.sub}
                        </span>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>

            {/* Nav */}
            <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
              {step > 1 && (
                <button onClick={goBack} style={btnStyle('rgba(255,255,255,0.07)', true)}>
                  ← Quay lại
                </button>
              )}
              <button
                onClick={goNext}
                disabled={!canProceed}
                style={{ ...btnStyle('#00D1FF'), flex: 1, opacity: canProceed ? 1 : 0.38, cursor: canProceed ? 'pointer' : 'not-allowed' }}
              >
                {step === totalSteps ? 'Xem kết quả →' : 'Tiếp theo →'}
              </button>
            </div>
          </div>
        )}

        {/* ── KẾT QUẢ ── */}
        {isResult && (
          <div>
            {/* Badge */}
            <div style={{ textAlign: 'center', marginBottom: 20 }}>
              <div style={{
                display: 'inline-block', padding: '5px 18px', borderRadius: 24,
                border: `1px solid ${profile.color}55`,
                background: `${profile.color}12`, marginBottom: 12,
              }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: profile.color, letterSpacing: '0.04em' }}>
                  {profile.label}
                </span>
              </div>
              <div style={{ fontSize: 19, fontWeight: 700, color: '#C8E4F8', lineHeight: 1.35, marginBottom: 8 }}>
                Hồ sơ rủi ro của bạn
              </div>
              <div style={{ fontSize: 13.5, color: 'rgba(160,200,235,0.65)', lineHeight: 1.65 }}>
                {profile.desc}
              </div>
            </div>

            {/* Score bar */}
            <div style={{ marginBottom: 20 }}>
              <div style={{ height: 6, borderRadius: 3, background: 'rgba(255,255,255,0.08)', overflow: 'hidden' }}>
                <div style={{
                  height: '100%', borderRadius: 3,
                  width: `${(avgScore / 5) * 100}%`,
                  background: `linear-gradient(90deg, #00D1FF, ${profile.color})`,
                  transition: 'width 0.9s ease',
                }} />
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 5 }}>
                <span style={{ fontSize: 10.5, color: 'rgba(160,200,235,0.35)' }}>Thận trọng</span>
                <span style={{ fontSize: 10.5, color: 'rgba(160,200,235,0.35)' }}>Tăng trưởng mạnh</span>
              </div>
            </div>

            {/* Gợi ý phân bổ */}
            <div style={{
              borderRadius: 12, padding: '12px 14px', marginBottom: 16,
              background: 'rgba(255,255,255,0.03)',
              border: '1px solid rgba(255,255,255,0.06)',
            }}>
              <div style={{ fontSize: 11, color: 'rgba(160,200,235,0.45)', marginBottom: 6, letterSpacing: '0.07em' }}>
                GỢI Ý PHÂN BỔ TÀI SẢN
              </div>
              <div style={{ fontSize: 12.5, color: 'rgba(160,200,235,0.75)', lineHeight: 1.6 }}>
                {profile.allocation}
              </div>
            </div>

            {/* MoBu sẽ dùng hồ sơ này để */}
            <div style={{
              borderRadius: 12, padding: '12px 14px', marginBottom: 20,
              background: `${profile.color}09`,
              border: `1px solid ${profile.color}22`,
            }}>
              <div style={{ fontSize: 11, color: profile.color, marginBottom: 10, letterSpacing: '0.07em', opacity: 0.8 }}>
                MoBu SẼ DÙNG HỒ SƠ NÀY ĐỂ
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                {profile.uses.map((use, i) => (
                  <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                    <div style={{
                      width: 5, height: 5, borderRadius: '50%', marginTop: 6, flexShrink: 0,
                      backgroundColor: profile.color, opacity: 0.7,
                    }} />
                    <span style={{ fontSize: 12.5, color: 'rgba(160,200,235,0.7)', lineHeight: 1.55 }}>
                      {use}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            <div style={{ fontSize: 11.5, color: 'rgba(160,200,235,0.4)', textAlign: 'center', marginBottom: 18 }}>
              Bạn có thể cập nhật hồ sơ rủi ro bất kỳ lúc nào trong Cài đặt.
            </div>

            <button onClick={handleFinish} disabled={saving} style={{ ...btnStyle('#00D1FF'), opacity: saving ? 0.6 : 1 }}>
              {saving ? 'Đang lưu...' : 'Vào không gian gia sản →'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Button style helper ───────────────────────────────────────────────────────
function btnStyle(bg: string, ghost = false): React.CSSProperties {
  return {
    width: ghost ? 'auto' : '100%',
    padding: ghost ? '11px 16px' : '13px',
    borderRadius: 11,
    border: ghost ? '1px solid rgba(255,255,255,0.1)' : 'none',
    background: ghost ? 'transparent' : bg,
    color: ghost ? 'rgba(200,228,248,0.65)' : (bg === '#00D1FF' ? '#0A192F' : '#C8E4F8'),
    fontSize: 14, fontWeight: 600,
    cursor: 'pointer',
    fontFamily: "'Inter', -apple-system, sans-serif",
    transition: 'opacity 0.15s ease',
    letterSpacing: '0.02em',
  };
}
